import { FC } from "react";
import { Helmet } from "react-helmet-async";
import BgGlassmorphism from "components/BgGlassmorphism/BgGlassmorphism";
import { Link } from "react-router-dom";

import ProductImg1 from "images/Devi-Plastic/3-Heavy-Sink-Coupling.webp";
import ProductImg2 from "images/Devi-Plastic/3-LITE-MS-SS-SKREW.webp";
import ProductImg3 from "images/Devi-Plastic/4 -Heavy-with-Reducer.webp";
import ProductImg4 from "images/Devi-Plastic/4 -JUMBO.webp";
import ProductImg5 from "images/Devi-Plastic/4-HEAVY-ROYAL.webp";
import ProductImg6 from "images/Devi-Plastic/4-LITE-COUPLING.webp";

export interface PageAboutProps {
  className?: string;
}

const dataofProducts: any[] = [
  {
    id: 1,
    name: "3 inch Heavy Sink Coupling",
    description:
      "Extra heavy sink coupling designed for strong grip and long-lasting drainage performance.",
    price: 99,
    image: ProductImg1,
    link: "/product/3-inch-heavy-sink-coupling",
    sizes: ["3 inch"],
    status: "Best Seller",
  },
  {
    id: 2,
    name: "3 inch Lite MS & SS Screw Coupling",
    description:
      "Lightweight sink coupling with MS & SS screws for easy installation and secure fitting.",
    price: 79,
    image: ProductImg2,
    link: "/product/3-inch-lite-ms-ss-screw",
    sizes: ["3 inch"],
    status: "New in",
  },
  {
    id: 3,
    name: "4 inch Heavy Coupling with Reducer",
    description:
      "Heavy quality sink coupling with reducer, ideal for flexible pipe connections.",
    price: 119,
    image: ProductImg3,
    link: "/product/4-inch-heavy-coupling-with-reducer",
    sizes: ["4 inch"],
    status: "New in",
  },
  {
    id: 4,
    name: "4 inch Jumbo Sink Coupling",
    description:
      "Jumbo size sink coupling suitable for high water flow and commercial usage.",
    price: 139,
    image: ProductImg4,
    link: "/product/4-inch-jumbo-sink-coupling",
    sizes: ["4 inch"],
    status: "Trending",
  },
  {
    id: 5,
    name: "4 inch Heavy Royal Sink Coupling",
    description:
      "Premium royal finish heavy sink coupling with superior strength and elegant design.",
    price: 149,
    image: ProductImg5,
    link: "/product/4-inch-heavy-royal-sink-coupling",
    sizes: ["4 inch"],
    status: "Best Seller",
  },
  {
    id: 6,
    name: "4 inch Lite Sink Coupling",
    description:
      "Lightweight 4-inch sink coupling for regular household drainage needs.",
    price: 89,
    image: ProductImg6,
    link: "/product/4-inch-lite-sink-coupling",
    sizes: ["4 inch"],
    status: "New in",
  },
];

const PageOurProduct: FC<PageAboutProps> = ({ className = "" }) => {
  return (
    <div
      className={`nc-PageAbout relative overflow-hidden ${className}`}
      data-nc-id="PageOurProduct"
    >
      <Helmet>
        {/* TITLE */}
        <title>
          Sink Couplings & Drainage Fittings | Our Products | Devi Plastic
          Ahmedabad
        </title>

        {/* META DESCRIPTION */}
        <meta
          name="description"
          content="Explore premium sink couplings by Devi Plastic – heavy, lite, jumbo & royal sink waste couplings designed for durability and perfect drainage. Trusted manufacturer from Ahmedabad, Gujarat."
        />

        {/* KEYWORDS */}
        <meta
          name="keywords"
          content="sink coupling products, plastic sink coupling, heavy sink coupling, lite sink coupling, jumbo sink coupling, royal sink coupling, drainage fittings manufacturer, kitchen sink coupling, bathroom sink waste coupling, Ahmedabad plastic manufacturer, Devi Plastic products"
        />

        {/* CANONICAL */}
        <link rel="canonical" href="https://www.deviplastic.in/products" />

        {/* OPEN GRAPH */}
        <meta
          property="og:title"
          content="Our Products | Premium Sink Couplings by Devi Plastic"
        />
        <meta
          property="og:description"
          content="Discover heavy, lite, jumbo & royal sink couplings manufactured by Devi Plastic – trusted drainage fittings brand from Ahmedabad."
        />
        <meta property="og:type" content="website" />
        <meta property="og:url" content="https://www.deviplastic.in/products" />
        {/* <meta
          property="og:image"
          content="https://www.deviplastic.in/og-products.jpg"
        /> */}

        {/* TWITTER */}
        <meta name="twitter:card" content="summary_large_image" />
        <meta
          name="twitter:title"
          content="Devi Plastic | Sink Coupling Product Range"
        />
        <meta
          name="twitter:description"
          content="Premium quality sink couplings for kitchen & bathroom drainage – manufactured in Ahmedabad."
        />

        {/* STRUCTURED DATA – PRODUCT LIST */}
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "ItemList",
            name: "Devi Plastic Sink Couplings",
            itemListElement: dataofProducts.map(
              (product: any, index: number) => ({
                "@type": "Product",
                position: index + 1,
                name: product.name,
                description: product.description,
                image: `https://www.deviplastic.in${product.image}`,
                url: `https://www.deviplastic.in${product.link}`,
                brand: {
                  "@type": "Brand",
                  name: "Devi Plastic",
                },
                offers: {
                  "@type": "Offer",
                  priceCurrency: "INR",
                  price: product.price,
                  availability: "https://schema.org/InStock",
                },
              })
            ),
          })}
        </script>
      </Helmet>
      {/* Background */}
      <BgGlassmorphism />
      {/* Page Content */}
      <div className="container relative py-20">
        {/* Heading */}
        <div className="text-center max-w-2xl mx-auto mb-14">
          <h1 className="text-3xl md:text-4xl font-bold text-neutral-900 dark:text-white">
            Our Products
          </h1>
          <p className="mt-4 text-neutral-600 dark:text-neutral-300">
            Premium quality sink couplings designed for durability, strength,
            and perfect drainage performance.
          </p>
        </div>

        {/* Product Grid */}
        <div className="grid gap-8 sm:grid-cols-2 lg:grid-cols-3">
          {dataofProducts.map((product) => (
            <Link
              to={product.link}
              key={product.id}
              className="group bg-white/80 dark:bg-neutral-900/70 backdrop-blur rounded-2xl shadow-md hover:shadow-xl transition-all duration-300 overflow-hidden"
            >
              {/* Image */}
              <div className="relative bg-neutral-100 dark:bg-neutral-800 p-6">
                <img
                  src={product.image}
                  alt={product.name}
                  className="w-full h-48 object-contain group-hover:scale-105 transition-transform duration-300"
                />

                {/* Status badge */}
                {product.status && (
                  <span className="absolute top-4 left-4 text-xs font-semibold px-3 py-1 rounded-full bg-primary-500 text-white">
                    {product.status}
                  </span>
                )}
              </div>

              {/* Content */}
              <div className="p-6">
                <h3 className="text-lg font-semibold text-neutral-900 dark:text-white">
                  {product.name}
                </h3>

                <p className="mt-2 text-sm text-neutral-600 dark:text-neutral-400 line-clamp-2">
                  {product.description}
                </p>

                {/* Size & Price */}
                <div className="mt-4 flex items-center justify-between">
                  <span className="text-sm text-neutral-500">
                    Size: {product.sizes.join(", ")}
                  </span>
                  {/* <span className="text-lg font-bold text-primary-600">
                    ₹{product.price}
                  </span> */}
                </div>

                {/* CTA */}
                <Link
                  to={product.link}
                  className="mt-5 inline-flex w-full items-center justify-center rounded-lg border border-primary-500 px-4 py-2 text-sm font-semibold text-primary-600 hover:bg-primary-500 hover:text-white transition-colors"
                >
                  View Details
                </Link>
              </div>
            </Link>
          ))}
        </div>
      </div>
    </div>
  );
};

export default PageOurProduct;
